import { useEffect, useState } from "react";
import { formatDate } from "../../utils/datetime";
import { useNavigate } from "react-router-dom";
import ManagementAPI from "./managementAPI";
import OrgSelect from "./OrgSelect";
import { Alert, CheckboxField, Modal, SelectField, TextField } from "./crudkit";

const statusClass = (s) => ({
    active: "ok", ACTIVE: "ok", canceled: "danger", CANCELLED: "danger",
    past_due: "warn", EXPIRED: "warn", trialing: "info", TRIAL: "info",
}[s] || "neutral");

const BILLING_CYCLES = ["monthly", "yearly"];
const SUB_STATUSES = ["active", "trialing", "past_due", "canceled"];
const INVOICE_STATUSES = ["open", "paid", "void", "uncollectible", "refunded"];
const fmtLimit = (v) => (v === -1 ? "Unlimited" : v?.toLocaleString?.() ?? v);
const usd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const invStatusClass = (s) => ({
    paid: "ok", open: "info", void: "neutral", uncollectible: "warn", refunded: "warn",
}[s] || "neutral");

// Compact subtotal / discount / tax breakdown line under an invoice amount.
const invBreakdown = (inv) => {
    const hasDisc = inv.discount_cents > 0;
    const taxes = inv.tax_breakdown || [];
    if (!hasDisc && taxes.length === 0) return null;
    const money = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
    return (
        <div className="mg-td-muted" style={{ fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>
            <div>Subtotal {money(inv.subtotal_cents)}</div>
            {hasDisc && <div>Discount −{money(inv.discount_cents)}</div>}
            {taxes.map((t, i) => <div key={i}>{t.name} {t.rate_pct}% {money(t.amount_cents)}</div>)}
            {inv.tax_inclusive && taxes.length > 0 && <div style={{ fontStyle: "italic" }}>(tax-inclusive)</div>}
        </div>
    );
};

export default function PlatformBilling() {
    const navigate = useNavigate();
    const [data,    setData]    = useState(null);
    const [plans,   setPlans]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState("");
    const [msg,     setMsg]     = useState("");
    const [search,  setSearch]  = useState("");

    // Assign/change-plan modal
    const [modal, setModal]         = useState(false);
    const [form, setForm]           = useState({ organization_id: "", plan_id: "", billing_cycle: "monthly", status: "active" });
    const [saving, setSaving]       = useState(false);
    const [formError, setFormError] = useState("");
    const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // Plan editor modal (create / edit plan limits)
    const emptyPlan = {
        id: null, name: "", display_name: "", price_monthly_usd: 0, price_yearly_usd: 0,
        max_conversations_month: 1000, max_agents: 3, max_intents: 100, max_channels: 1,
        max_domains: -1, max_kb_articles: -1, max_flows: -1, max_widgets: -1,
        max_messages_month: -1, max_api_calls_month: -1, max_storage_mb: -1,
        allow_api_access: false, allow_custom_branding: false, allow_sso: false,
        allow_flow_builder: true, allow_live_agent: true, allow_omnichannel: true,
        allow_advanced_analytics: true, allow_llm_fallback: true, allow_csat: true,
        trial_days: 0, grace_days: 0,
    };
    const [planModal, setPlanModal]   = useState(false);
    const [planForm, setPlanForm]     = useState(emptyPlan);
    const [planSaving, setPlanSaving] = useState(false);
    const [planError, setPlanError]   = useState("");
    const setPF = (k, v) => setPlanForm(f => ({ ...f, [k]: v }));

    // Global billing settings (tax / discount / auto-invoice)
    const emptyBset = {
        taxes: [], tax_inclusive: false, discount_type: "none", discount_value: 0,
        currency: "usd", auto_invoice_enabled: true, invoice_lead_days: 1, payment_grace_days: 1,
    };
    const [bset, setBset]         = useState(emptyBset);
    const [bsetSaving, setBsetSaving] = useState(false);
    const [bsetMsg, setBsetMsg]   = useState("");
    const [runningCycle, setRunningCycle] = useState(false);
    const setBS = (k, v) => setBset(s => ({ ...s, [k]: v }));

    // Platform email / SMTP settings
    const emptyEset = {
        enabled: false, host: "", port: 587, username: "", password: "",
        has_password: false, from_email: "", from_name: "", use_tls: true,
    };
    const [eset, setEset]         = useState(emptyEset);
    const [esetSaving, setEsetSaving] = useState(false);
    const [esetMsg, setEsetMsg]   = useState("");
    const [esetTesting, setEsetTesting] = useState(false);
    const setES = (k, v) => setEset(s => ({ ...s, [k]: v }));

    // Platform Meta app — WhatsApp Embedded Signup for every organization
    const emptyMset = {
        enabled: false, app_id: "", config_id: "", graph_version: "v21.0",
        app_secret: "", app_secret_set: false, env_fallback: null,
    };
    const [mset, setMset]         = useState(emptyMset);
    const [msetSaving, setMsetSaving] = useState(false);
    const [msetMsg, setMsetMsg]   = useState("");
    const setMS = (k, v) => setMset(s => ({ ...s, [k]: v }));

    // Invoices & payments (per-org)
    const [invOrg, setInvOrg]         = useState("");
    const [invData, setInvData]       = useState(null);
    const [invLoading, setInvLoading] = useState(false);
    const [invModal, setInvModal]     = useState(false);
    const emptyInvoice = { amount_cents: "", currency: "usd", status: "open", period_start: "", period_end: "", due_date: "", invoice_pdf_url: "", send_email: false };
    const [invForm, setInvForm]       = useState(emptyInvoice);
    const [invSaving, setInvSaving]   = useState(false);
    const [invError, setInvError]     = useState("");
    const setIF = (k, v) => setInvForm(f => ({ ...f, [k]: v }));

    const loadInvoices = (orgId) => {
        if (!orgId) { setInvData(null); return; }
        setInvLoading(true);
        ManagementAPI.listOrgInvoices(orgId)
            .then(d => setInvData(d))
            .catch(e => { setInvData(null); setError(e.message); })
            .finally(() => setInvLoading(false));
    };

    const pickInvOrg = (orgId) => { setInvOrg(orgId); loadInvoices(orgId); };

    const handleGenerateInvoice = async () => {
        setInvSaving(true); setInvError("");
        try {
            if (!invOrg) throw new Error("Select an organization first.");
            const res = await ManagementAPI.createOrgInvoice(Number(invOrg), {
                amount_cents: invForm.amount_cents === "" ? undefined : Number(invForm.amount_cents),
                currency: invForm.currency || "usd",
                status: invForm.status,
                period_start: invForm.period_start || undefined,
                period_end: invForm.period_end || undefined,
                due_date: invForm.due_date || undefined,
                invoice_pdf_url: invForm.invoice_pdf_url || undefined,
                send_email: !!invForm.send_email,
            });
            setMsg(invForm.send_email
                ? (res?.email_sent ? "Invoice generated and emailed to the organization." : "Invoice generated. Email could not be sent — check SMTP settings.")
                : "Invoice generated.");
            setInvModal(false); setInvForm(emptyInvoice); loadInvoices(invOrg);
        } catch (e) { setInvError(e.message); } finally { setInvSaving(false); }
    };

    const changeInvoiceStatus = async (invoiceId, status) => {
        try {
            await ManagementAPI.updateInvoiceStatus(invoiceId, { status });
            setMsg(`Invoice marked ${status}.`);
            loadInvoices(invOrg);
        } catch (e) { setError(e.message); }
    };

    const reloadPlans = () => ManagementAPI.listPlans().then(d => setPlans(d.plans || [])).catch(() => {});

    const openPlan = (p = null) => {
        setPlanForm(p ? { ...emptyPlan, ...p } : emptyPlan);
        setPlanError(""); setPlanModal(true);
    };

    const handleSavePlan = async () => {
        setPlanSaving(true); setPlanError("");
        try {
            const body = {
                display_name: planForm.display_name.trim() || planForm.name.trim(),
                price_monthly_usd: Number(planForm.price_monthly_usd) || 0,
                price_yearly_usd: Number(planForm.price_yearly_usd) || 0,
                max_conversations_month: Number(planForm.max_conversations_month),
                max_agents: Number(planForm.max_agents),
                max_intents: Number(planForm.max_intents),
                max_channels: Number(planForm.max_channels),
                max_domains: Number(planForm.max_domains),
                max_kb_articles: Number(planForm.max_kb_articles),
                max_flows: Number(planForm.max_flows),
                max_widgets: Number(planForm.max_widgets),
                max_messages_month: Number(planForm.max_messages_month),
                max_api_calls_month: Number(planForm.max_api_calls_month),
                max_storage_mb: Number(planForm.max_storage_mb),
                allow_api_access: !!planForm.allow_api_access,
                allow_custom_branding: !!planForm.allow_custom_branding,
                allow_sso: !!planForm.allow_sso,
                allow_flow_builder: !!planForm.allow_flow_builder,
                allow_live_agent: !!planForm.allow_live_agent,
                allow_omnichannel: !!planForm.allow_omnichannel,
                allow_advanced_analytics: !!planForm.allow_advanced_analytics,
                allow_llm_fallback: !!planForm.allow_llm_fallback,
                allow_csat: !!planForm.allow_csat,
                trial_days: Number(planForm.trial_days),
                grace_days: Number(planForm.grace_days),
            };
            if (planForm.id) {
                await ManagementAPI.updatePlan(planForm.id, body);
                setMsg(`Plan "${planForm.name}" updated.`);
            } else {
                if (!planForm.name.trim()) throw new Error("Plan key (name) is required, e.g. 'pro'.");
                await ManagementAPI.createPlan({ name: planForm.name.trim().toLowerCase(), ...body });
                setMsg(`Plan "${planForm.name}" created.`);
            }
            setPlanModal(false); reloadPlans();
        } catch (e) { setPlanError(e.message); } finally { setPlanSaving(false); }
    };

    const load = () => {
        setLoading(true);
        ManagementAPI.platformBilling()
            .then(d => { setData(d); setLoading(false); })
            .catch(err => {
                if (err.message?.includes("401")) navigate("/management/login");
                setError(err.message); setLoading(false);
            });
    };

    useEffect(() => {
        load();
        ManagementAPI.listPlans().then(d => setPlans(d.plans || [])).catch(() => { /* non-fatal */ });
        ManagementAPI.getBillingSettings()
            .then(d => setBset({ ...emptyBset, ...d, taxes: d.taxes || [] }))
            .catch(() => { /* non-fatal */ });
        ManagementAPI.getEmailSettings()
            .then(d => setEset({ ...emptyEset, ...d, password: "" }))
            .catch(() => { /* non-fatal */ });
        ManagementAPI.getMetaSettings()
            .then(d => setMset({ ...emptyMset, ...d, app_secret: "" }))
            .catch(() => { /* non-fatal */ });
    }, []);

    // ── Platform Meta app (WhatsApp Embedded Signup) ──
    const saveMset = async () => {
        setMsetSaving(true); setMsetMsg("");
        try {
            const body = {
                enabled: !!mset.enabled,
                app_id: (mset.app_id || "").trim(),
                config_id: (mset.config_id || "").trim(),
                graph_version: (mset.graph_version || "v21.0").trim(),
            };
            // Write-only: send the secret only when a new one was typed.
            if (mset.app_secret) body.app_secret = mset.app_secret;
            const d = await ManagementAPI.updateMetaSettings(body);
            setMset({ ...emptyMset, ...d, app_secret: "" });
            setMsetMsg("WhatsApp Embedded Signup settings saved.");
        } catch (e) {
            setMsetMsg(e.message || "Could not save settings.");
        } finally {
            setMsetSaving(false);
        }
    };

    // ── Global billing settings handlers ──
    const addTax    = () => setBset(s => ({ ...s, taxes: [...(s.taxes || []), { name: "", rate_pct: 0 }] }));
    const removeTax = (i) => setBset(s => ({ ...s, taxes: s.taxes.filter((_, idx) => idx !== i) }));
    const setTax    = (i, k, v) => setBset(s => ({ ...s, taxes: s.taxes.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
    const saveBset = async () => {
        setBsetSaving(true); setBsetMsg("");
        try {
            const body = {
                ...bset,
                discount_value: Number(bset.discount_value) || 0,
                invoice_lead_days: Number(bset.invoice_lead_days) || 0,
                payment_grace_days: Number(bset.payment_grace_days) || 0,
                taxes: (bset.taxes || [])
                    .filter(t => (t.name || "").trim())
                    .map(t => ({ name: t.name.trim(), rate_pct: Number(t.rate_pct) || 0 })),
            };
            const saved = await ManagementAPI.updateBillingSettings(body);
            setBset({ ...emptyBset, ...saved, taxes: saved.taxes || [] });
            setBsetMsg("Billing settings saved.");
        } catch (e) { setBsetMsg(e.message); } finally { setBsetSaving(false); }
    };
    const runCycleNow = async () => {
        if (!confirm("Run the billing cycle now? This generates renewal invoices for any org due within the lead window.")) return;
        setRunningCycle(true); setBsetMsg("");
        try {
            const r = await ManagementAPI.runBillingCycle();
            setBsetMsg(`Billing cycle ran — ${r.created} invoice(s) generated.`);
            if (invOrg) loadInvoices(invOrg);
        } catch (e) { setBsetMsg(e.message); } finally { setRunningCycle(false); }
    };

    // ── Email / SMTP settings handlers ──
    const saveEset = async () => {
        setEsetSaving(true); setEsetMsg("");
        try {
            const body = {
                enabled: !!eset.enabled,
                host: eset.host || "",
                port: Number(eset.port) || 587,
                username: eset.username || "",
                from_email: eset.from_email || "",
                from_name: eset.from_name || "",
                use_tls: !!eset.use_tls,
            };
            // Only send password when the admin typed a new one (write-only).
            if (eset.password) body.password = eset.password;
            const saved = await ManagementAPI.updateEmailSettings(body);
            setEset({ ...emptyEset, ...saved, password: "" });
            setEsetMsg("Email settings saved.");
        } catch (e) { setEsetMsg(e.message); } finally { setEsetSaving(false); }
    };
    const testEset = async () => {
        const to = prompt("Send a test email to which address?", eset.from_email || "");
        if (!to) return;
        setEsetTesting(true); setEsetMsg("");
        try {
            await ManagementAPI.testEmailSettings(to);
            setEsetMsg(`Test email sent to ${to}.`);
        } catch (e) { setEsetMsg(e.message); } finally { setEsetTesting(false); }
    };

    const openAssign = (sub = null) => {
        setForm({
            organization_id: sub ? String(sub.organization_id) : "",
            plan_id: sub?.plan_id ? String(sub.plan_id) : (plans[0]?.id ? String(plans[0].id) : ""),
            billing_cycle: sub?.billing_cycle || "monthly",
            status: sub?.status || "active",
        });
        setFormError(""); setModal(true);
    };

    const handleSave = async () => {
        setSaving(true); setFormError("");
        try {
            if (!form.organization_id) throw new Error("Select an organization.");
            if (!form.plan_id) throw new Error("Select a plan.");
            await ManagementAPI.setOrgSubscription(Number(form.organization_id), {
                plan_id: Number(form.plan_id),
                billing_cycle: form.billing_cycle,
                status: form.status,
            });
            setMsg("Plan updated for the organization.");
            setModal(false); load();
        } catch (e) { setFormError(e.message); } finally { setSaving(false); }
    };

    const filtered = data?.subscriptions?.filter(s =>
        !search ||
        String(s.organization_id).includes(search) ||
        (s.organization_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (s.plan || "").toLowerCase().includes(search.toLowerCase())
    ) || [];

    const statusCounts = (data?.subscriptions || []).reduce((acc, s) => {
        acc[s.status] = (acc[s.status] || 0) + 1;
        return acc;
    }, {});

    const selectedPlan = plans.find(p => String(p.id) === String(form.plan_id));

    return (
        <div>
            <div className="mg-page-head">
                <div>
                    <h1 className="mg-h1">Billing</h1>
                    <p className="mg-sub">Per-organization plans &amp; subscriptions.</p>
                </div>
                <button className="mg-btn mg-btn-primary" onClick={() => openAssign(null)}>+ Assign / Change Plan</button>
            </div>

            {msg   && <Alert type="success" msg={msg} onClose={() => setMsg("")} />}
            {error && <div className="mg-alert error" style={{ justifyContent: "flex-start" }}>{error}</div>}

            {loading ? (
                <div className="mg-loading">Loading…</div>
            ) : data && (
                <>
                    <div className="mg-stat-grid">
                        <Stat label="Total Subscriptions" value={data.total} />
                        <Stat label="Active" value={statusCounts.active || statusCounts.ACTIVE || 0} tone="ok" />
                        <Stat label="Trialing" value={statusCounts.trialing || statusCounts.TRIAL || 0} tone="violet" />
                        <Stat label="Canceled" value={statusCounts.canceled || statusCounts.CANCELLED || 0} tone="warn" />
                    </div>

                    {/* ── Global Billing Settings (tax / discount / auto-invoice) ── */}
                    <div className="mg-card mg-card-pad" style={{ marginBottom: 16 }}>
                        <div className="mg-page-head" style={{ marginBottom: 8 }}>
                            <div>
                                <h1 className="mg-h1" style={{ fontSize: 18 }}>Billing Settings</h1>
                                <p className="mg-sub">Platform-wide tax, discount &amp; auto-invoice defaults. Orgs can override these individually.</p>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button className="mg-btn mg-btn-ghost" onClick={runCycleNow} disabled={runningCycle} title="Generate any due renewal invoices now">
                                    {runningCycle ? "Running…" : "Run Billing Now"}
                                </button>
                                <button className="mg-btn mg-btn-primary" onClick={saveBset} disabled={bsetSaving}>
                                    {bsetSaving ? "Saving…" : "Save Settings"}
                                </button>
                            </div>
                        </div>
                        {bsetMsg && <div className="mg-alert" style={{ justifyContent: "flex-start", marginBottom: 10 }}>{bsetMsg}</div>}

                        <h4 className="mg-form-section">Taxes</h4>
                        <p className="mg-sub" style={{ marginTop: -4 }}>Add one row per tax component (e.g. CGST 9% + SGST 9%, or GST 18%).</p>
                        {(bset.taxes || []).map((t, i) => (
                            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 8 }}>
                                <div style={{ flex: 2 }}>
                                    <TextField label={i === 0 ? "Tax name" : ""} value={t.name} onChange={v => setTax(i, "name", v)} placeholder="e.g. CGST" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <TextField label={i === 0 ? "Rate %" : ""} type="number" value={t.rate_pct} onChange={v => setTax(i, "rate_pct", v)} placeholder="9" />
                                </div>
                                <button className="mg-btn mg-btn-ghost mg-btn-sm" style={{ marginBottom: 2 }} onClick={() => removeTax(i)}>Remove</button>
                            </div>
                        ))}
                        <button className="mg-btn mg-btn-ghost mg-btn-sm" onClick={addTax}>+ Add Tax</button>
                        <div style={{ marginTop: 10 }}>
                            <CheckboxField label="Prices are tax-inclusive (tax already included in plan price)" checked={bset.tax_inclusive} onChange={v => setBS("tax_inclusive", v)} />
                        </div>

                        <h4 className="mg-form-section">Discount</h4>
                        <SelectField label="Discount type" value={bset.discount_type} onChange={v => setBS("discount_type", v)}
                            options={[{ value: "none", label: "None" }, { value: "percent", label: "Percentage (%)" }, { value: "fixed", label: "Fixed amount" }]} />
                        {bset.discount_type !== "none" && (
                            <TextField
                                label={bset.discount_type === "percent" ? "Discount %" : `Discount amount (${(bset.currency || "usd").toUpperCase()} cents)`}
                                type="number" value={bset.discount_value} onChange={v => setBS("discount_value", v)} />
                        )}

                        <h4 className="mg-form-section">Auto-invoice &amp; payment</h4>
                        <CheckboxField label="Auto-generate invoices before each renewal" checked={bset.auto_invoice_enabled} onChange={v => setBS("auto_invoice_enabled", v)} />
                        <TextField label="Generate invoice this many days before renewal" type="number" value={bset.invoice_lead_days} onChange={v => setBS("invoice_lead_days", v)} />
                        <TextField label="Payment grace days (unpaid past due + this → blocked)" type="number" value={bset.payment_grace_days} onChange={v => setBS("payment_grace_days", v)} />
                        <TextField label="Currency (ISO, e.g. usd, inr)" value={bset.currency} onChange={v => setBS("currency", v)} />
                    </div>

                    {/* ── Email / SMTP Settings (used to email invoices & notifications) ── */}
                    <div className="mg-card mg-card-pad" style={{ marginBottom: 16 }}>
                        <div className="mg-page-head" style={{ marginBottom: 8 }}>
                            <div>
                                <h1 className="mg-h1" style={{ fontSize: 18 }}>Email / SMTP Settings</h1>
                                <p className="mg-sub">Outbound mail server used to email invoices and other notifications. Leave disabled to skip emails.</p>
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button className="mg-btn mg-btn-ghost" onClick={testEset} disabled={esetTesting || !eset.enabled} title="Send a test email using the saved settings">
                                    {esetTesting ? "Sending…" : "Send Test Email"}
                                </button>
                                <button className="mg-btn mg-btn-primary" onClick={saveEset} disabled={esetSaving}>
                                    {esetSaving ? "Saving…" : "Save Settings"}
                                </button>
                            </div>
                        </div>
                        {esetMsg && <div className="mg-alert" style={{ justifyContent: "flex-start", marginBottom: 10 }}>{esetMsg}</div>}

                        <CheckboxField label="Enable outbound email (SMTP)" checked={eset.enabled} onChange={v => setES("enabled", v)} />
                        <div style={{ display: "flex", gap: 8 }}>
                            <div style={{ flex: 3 }}>
                                <TextField label="SMTP host" value={eset.host} onChange={v => setES("host", v)} placeholder="smtp.gmail.com" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <TextField label="Port" type="number" value={eset.port} onChange={v => setES("port", v)} placeholder="587" />
                            </div>
                        </div>
                        <TextField label="Username" value={eset.username} onChange={v => setES("username", v)} placeholder="apikey / user@example.com" />
                        <TextField
                            label={eset.has_password ? "Password (leave blank to keep current)" : "Password"}
                            type="password" value={eset.password} onChange={v => setES("password", v)}
                            placeholder={eset.has_password ? "•••••••• (unchanged)" : "SMTP password / app key"} />
                        <div style={{ display: "flex", gap: 8 }}>
                            <div style={{ flex: 2 }}>
                                <TextField label="From email" value={eset.from_email} onChange={v => setES("from_email", v)} placeholder="billing@yourcompany.com" />
                            </div>
                            <div style={{ flex: 2 }}>
                                <TextField label="From name" value={eset.from_name} onChange={v => setES("from_name", v)} placeholder="YourCompany Billing" />
                            </div>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <CheckboxField label="Use STARTTLS (recommended for port 587)" checked={eset.use_tls} onChange={v => setES("use_tls", v)} />
                        </div>
                    </div>

                    {/* ── WhatsApp Embedded Signup (one Meta app onboards every organization) ── */}
                    <div className="mg-card mg-card-pad" style={{ marginBottom: 16 }}>
                        <div className="mg-page-head" style={{ marginBottom: 8 }}>
                            <div>
                                <h1 className="mg-h1" style={{ fontSize: 18 }}>WhatsApp Embedded Signup (Meta app)</h1>
                                <p className="mg-sub">
                                    One Meta app connects WhatsApp for every organization: the customer clicks
                                    “Connect WhatsApp” in their console, signs in with their own Facebook account
                                    and picks their own number. Set it once here — no redeploy needed.
                                </p>
                            </div>
                            <button className="mg-btn mg-btn-primary" onClick={saveMset} disabled={msetSaving}>
                                {msetSaving ? "Saving…" : "Save Settings"}
                            </button>
                        </div>
                        {msetMsg && <div className="mg-alert" style={{ justifyContent: "flex-start", marginBottom: 10 }}>{msetMsg}</div>}

                        <CheckboxField label="Enable Embedded Signup for all organizations" checked={mset.enabled} onChange={v => setMS("enabled", v)} />
                        <div style={{ display: "flex", gap: 8 }}>
                            <div style={{ flex: 2 }}>
                                <TextField label="Meta App ID" value={mset.app_id} onChange={v => setMS("app_id", v)} placeholder="1234567890123456" />
                            </div>
                            <div style={{ flex: 2 }}>
                                <TextField label="Embedded Signup Config ID" value={mset.config_id} onChange={v => setMS("config_id", v)} placeholder="1588572569673601" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <TextField label="Graph version" value={mset.graph_version} onChange={v => setMS("graph_version", v)} placeholder="v21.0" />
                            </div>
                        </div>
                        <TextField
                            label={mset.app_secret_set ? "App Secret (leave blank to keep current)" : "App Secret"}
                            type="password" value={mset.app_secret} onChange={v => setMS("app_secret", v)}
                            placeholder={mset.app_secret_set ? "•••••••• (unchanged)" : "Meta App → Settings → Basic → App Secret"} />
                        {mset.env_fallback?.app_id && !mset.app_id && (
                            <div className="mg-sub" style={{ marginTop: 6 }}>
                                Currently falling back to the server environment (App ID {mset.env_fallback.app_id}
                                {mset.env_fallback.app_secret_set ? ", secret set" : ", secret missing"}). Values saved here take priority.
                            </div>
                        )}
                    </div>

                    <div className="mg-toolbar">
                        <div className="mg-field">
                            <span className="mg-field-label">Search</span>
                            <input className="mg-input mg-inline" style={{ width: 260 }} placeholder="Org name, ID or plan…" value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        {search && <button className="mg-clear" onClick={() => setSearch("")}>Clear</button>}
                        <span className="mg-count">{filtered.length} result{filtered.length !== 1 ? "s" : ""}</span>
                    </div>

                    <div className="mg-card">
                        <div className="mg-table-wrap">
                            <table className="mg-table">
                                <thead>
                                    <tr>{["S.No", "Organization", "Plan", "Billing Cycle", "Status", "Period End", "Actions"].map(h => <th key={h}>{h}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 && <tr><td colSpan={7} className="mg-empty">No subscriptions found</td></tr>}
                                    {filtered.map((s, i) => (
                                        <tr key={s.id}>
                                            <td className="mg-td-muted mg-num">{i + 1}</td>
                                            <td><button className="mg-link" onClick={() => navigate(`/management/org/${s.organization_id}`)}>{s.organization_name || `#${s.organization_id}`}</button></td>
                                            <td className="mg-td-strong" style={{ textTransform: "capitalize" }}>{s.plan || "—"}</td>
                                            <td className="mg-td-muted" style={{ textTransform: "capitalize" }}>{s.billing_cycle || "—"}</td>
                                            <td><span className={`mg-badge ${statusClass(s.status)}`}>{s.status}</span></td>
                                            <td className="mg-td-muted mg-num" style={{ whiteSpace: "nowrap" }}>{s.current_period_end ? formatDate(s.current_period_end) : "—"}</td>
                                            <td><button className="mg-btn mg-btn-ghost mg-btn-sm" onClick={() => openAssign(s)}>Change plan</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ── Manage Plans (definitions & limits) ── */}
                    <div className="mg-page-head" style={{ marginTop: 22 }}>
                        <div>
                            <h1 className="mg-h1" style={{ fontSize: 18 }}>Plans &amp; Limits</h1>
                            <p className="mg-sub">Create plans and set their limits (-1 = unlimited).</p>
                        </div>
                        <button className="mg-btn mg-btn-primary" onClick={() => openPlan(null)}>+ Add Plan</button>
                    </div>
                    <div className="mg-card">
                        <div className="mg-table-wrap">
                            <table className="mg-table">
                                <thead>
                                    <tr>{["S.No", "Plan", "Conversations/mo", "Agents", "Intents", "Channels", "API", "Branding", "SSO", "Actions"].map(h => <th key={h}>{h}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {plans.length === 0 && <tr><td colSpan={10} className="mg-empty">No plans yet</td></tr>}
                                    {plans.map((p, i) => (
                                        <tr key={p.id}>
                                            <td className="mg-td-muted mg-num">{i + 1}</td>
                                            <td className="mg-td-strong">{p.display_name} <span className="mg-td-muted mg-mono">({p.name})</span></td>
                                            <td className="mg-num">{fmtLimit(p.max_conversations_month)}</td>
                                            <td className="mg-num">{fmtLimit(p.max_agents)}</td>
                                            <td className="mg-num">{fmtLimit(p.max_intents)}</td>
                                            <td className="mg-num">{fmtLimit(p.max_channels)}</td>
                                            <td><span className={`mg-badge ${p.allow_api_access ? "ok" : "neutral"}`}>{p.allow_api_access ? "Yes" : "No"}</span></td>
                                            <td><span className={`mg-badge ${p.allow_custom_branding ? "ok" : "neutral"}`}>{p.allow_custom_branding ? "Yes" : "No"}</span></td>
                                            <td><span className={`mg-badge ${p.allow_sso ? "ok" : "neutral"}`}>{p.allow_sso ? "Yes" : "No"}</span></td>
                                            <td><button className="mg-btn mg-btn-ghost mg-btn-sm" onClick={() => openPlan(p)}>Edit limits</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ── Invoices & Payments (per organization) ── */}
                    <div className="mg-page-head" style={{ marginTop: 22 }}>
                        <div>
                            <h1 className="mg-h1" style={{ fontSize: 18 }}>Invoices &amp; Payments</h1>
                            <p className="mg-sub">Generate invoices, record payments and view billing history per organization.</p>
                        </div>
                        <button
                            className="mg-btn mg-btn-primary"
                            disabled={!invOrg}
                            onClick={() => { setInvForm(emptyInvoice); setInvError(""); setInvModal(true); }}
                        >+ Generate Invoice</button>
                    </div>
                    <div className="mg-card mg-card-pad">
                        <div style={{ maxWidth: 420 }}>
                            <OrgSelect value={invOrg} onChange={pickInvOrg} label="Organization" />
                        </div>

                        {invOrg && invData && (
                            <div className="mg-stat-grid" style={{ marginTop: 12 }}>
                                <Stat label="Invoices" value={invData.total} />
                                <Stat label="Total Paid" value={usd(invData.total_paid_cents)} tone="ok" />
                                <Stat label="Outstanding" value={usd(invData.total_outstanding_cents)} tone="warn" />
                            </div>
                        )}
                    </div>

                    {invOrg && (
                        <div className="mg-card" style={{ marginTop: 12 }}>
                            <div className="mg-table-wrap">
                                {invLoading ? (
                                    <div className="mg-loading">Loading…</div>
                                ) : (
                                    <table className="mg-table">
                                        <thead>
                                            <tr>{["S.No", "Invoice #", "Date", "Amount", "Status", "Period", "Due", "PDF", "Actions"].map(h => <th key={h}>{h}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            {(!invData || invData.invoices.length === 0) && (
                                                <tr><td colSpan={9} className="mg-empty">No invoices yet for this organization</td></tr>
                                            )}
                                            {invData?.invoices.map((inv, i) => (
                                                <tr key={inv.id}>
                                                    <td className="mg-td-muted mg-num">{i + 1}</td>
                                                    <td className="mg-mono mg-td-muted">{inv.invoice_number || `#${inv.id}`}</td>
                                                    <td className="mg-td-muted mg-num" style={{ whiteSpace: "nowrap" }}>{formatDate(inv.created_at)}</td>
                                                    <td className="mg-td-strong mg-num">
                                                        {usd(inv.amount_cents)} <span className="mg-td-muted">{inv.currency?.toUpperCase()}</span>
                                                        {invBreakdown(inv)}
                                                    </td>
                                                    <td><span className={`mg-badge ${invStatusClass(inv.status)}`}>{inv.status}</span></td>
                                                    <td className="mg-td-muted" style={{ whiteSpace: "nowrap" }}>
                                                        {inv.period_start && inv.period_end
                                                            ? `${formatDate(inv.period_start)} – ${formatDate(inv.period_end)}`
                                                            : "—"}
                                                    </td>
                                                    <td className="mg-td-muted mg-num" style={{ whiteSpace: "nowrap" }}>{inv.due_date ? formatDate(inv.due_date) : "—"}</td>
                                                    <td>{inv.invoice_pdf_url
                                                        ? <a className="mg-link" href={inv.invoice_pdf_url} target="_blank" rel="noreferrer">PDF</a>
                                                        : <span className="mg-td-muted">—</span>}</td>
                                                    <td style={{ display: "flex", gap: 6 }}>
                                                        {inv.status !== "paid" && (
                                                            <button className="mg-btn mg-btn-ghost mg-btn-sm" onClick={() => changeInvoiceStatus(inv.id, "paid")}>Mark paid</button>
                                                        )}
                                                        {inv.status !== "void" && inv.status !== "paid" && (
                                                            <button className="mg-btn mg-btn-ghost mg-btn-sm" onClick={() => changeInvoiceStatus(inv.id, "void")}>Void</button>
                                                        )}
                                                        {inv.status === "paid" && (
                                                            <button className="mg-btn mg-btn-ghost mg-btn-sm" onClick={() => changeInvoiceStatus(inv.id, "refunded")}>Refund</button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

            {invModal && (
                <Modal
                    title="Generate Invoice"
                    onClose={() => setInvModal(false)} onSave={handleGenerateInvoice} saving={invSaving}
                    error={invError} saveLabel="Generate"
                >
                    <p className="mg-sub" style={{ marginTop: 0 }}>Leave amount blank to use the organization's current plan price.</p>
                    <TextField label="Amount (USD cents)" type="number" value={invForm.amount_cents} onChange={v => setIF("amount_cents", v)} placeholder="e.g. 4900 = $49.00" />
                    <TextField label="Currency" value={invForm.currency} onChange={v => setIF("currency", v)} placeholder="usd" />
                    <SelectField label="Status" value={invForm.status} onChange={v => setIF("status", v)} options={INVOICE_STATUSES} />
                    <TextField label="Period start (YYYY-MM-DD)" value={invForm.period_start} onChange={v => setIF("period_start", v)} placeholder="optional" />
                    <TextField label="Period end (YYYY-MM-DD)" value={invForm.period_end} onChange={v => setIF("period_end", v)} placeholder="optional" />
                    <TextField label="Payment due date (YYYY-MM-DD)" value={invForm.due_date} onChange={v => setIF("due_date", v)} placeholder="optional — defaults to period end" />
                    <TextField label="Invoice PDF URL" value={invForm.invoice_pdf_url} onChange={v => setIF("invoice_pdf_url", v)} placeholder="optional" />
                    <div style={{ marginTop: 8 }}>
                        <CheckboxField label="Email this invoice to the organization" checked={invForm.send_email} onChange={v => setIF("send_email", v)} />
                        <p className="mg-sub" style={{ marginTop: 2 }}>Requires SMTP to be configured &amp; enabled above. The org is always notified in-app.</p>
                    </div>
                    <p className="mg-sub" style={{ marginTop: 4 }}>Amount is the pre-tax base; the org's tax &amp; discount settings are applied automatically.</p>
                </Modal>
            )}

            {modal && (
                <Modal
                    title="Assign / Change Plan"
                    onClose={() => setModal(false)} onSave={handleSave} saving={saving}
                    error={formError} saveLabel="Save Plan"
                >
                    <OrgSelect value={form.organization_id} onChange={v => setF("organization_id", v)} />
                    <SelectField
                        label="Plan *"
                        value={form.plan_id}
                        onChange={v => setF("plan_id", v)}
                        options={[
                            { value: "", label: "— select a plan —" },
                            ...plans.map(p => ({ value: String(p.id), label: `${p.display_name} (${p.name})` })),
                        ]}
                    />
                    <SelectField label="Billing Cycle" value={form.billing_cycle} onChange={v => setF("billing_cycle", v)} options={BILLING_CYCLES} />
                    <SelectField label="Status" value={form.status} onChange={v => setF("status", v)} options={SUB_STATUSES} />

                    {selectedPlan && (
                        <div className="mg-card mg-card-pad" style={{ marginTop: 10 }}>
                            <div className="mg-field-label" style={{ marginBottom: 6 }}>Plan limits</div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 13 }}>
                                <span>Conversations/mo: <strong>{fmtLimit(selectedPlan.max_conversations_month)}</strong></span>
                                <span>Agents: <strong>{fmtLimit(selectedPlan.max_agents)}</strong></span>
                                <span>Intents: <strong>{fmtLimit(selectedPlan.max_intents)}</strong></span>
                                <span>Channels: <strong>{fmtLimit(selectedPlan.max_channels)}</strong></span>
                                <span>API access: <strong>{selectedPlan.allow_api_access ? "Yes" : "No"}</strong></span>
                                <span>Custom branding: <strong>{selectedPlan.allow_custom_branding ? "Yes" : "No"}</strong></span>
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {planModal && (
                <Modal
                    title={planForm.id ? `Edit Plan: ${planForm.name}` : "Add Plan"}
                    onClose={() => setPlanModal(false)} onSave={handleSavePlan} saving={planSaving}
                    error={planError} saveLabel={planForm.id ? "Save Limits" : "Create Plan"}
                >
                    {!planForm.id && (
                        <TextField label="Plan Key * (e.g. pro)" value={planForm.name} onChange={v => setPF("name", v)} placeholder="pro" />
                    )}
                    <TextField label="Display Name" value={planForm.display_name} onChange={v => setPF("display_name", v)} placeholder="Pro" />
                    <TextField label="Price / month (USD cents)" type="number" value={planForm.price_monthly_usd} onChange={v => setPF("price_monthly_usd", v)} />
                    <TextField label="Price / year (USD cents)" type="number" value={planForm.price_yearly_usd} onChange={v => setPF("price_yearly_usd", v)} />
                    <h4 className="mg-form-section">Resource limits (-1 = unlimited)</h4>
                    <TextField label="Max conversations / month" type="number" value={planForm.max_conversations_month} onChange={v => setPF("max_conversations_month", v)} />
                    <TextField label="Max agents" type="number" value={planForm.max_agents} onChange={v => setPF("max_agents", v)} />
                    <TextField label="Max intents" type="number" value={planForm.max_intents} onChange={v => setPF("max_intents", v)} />
                    <TextField label="Max channels" type="number" value={planForm.max_channels} onChange={v => setPF("max_channels", v)} />
                    <TextField label="Max domains (verticals)" type="number" value={planForm.max_domains} onChange={v => setPF("max_domains", v)} />
                    <TextField label="Max knowledge-base articles" type="number" value={planForm.max_kb_articles} onChange={v => setPF("max_kb_articles", v)} />
                    <TextField label="Max flows" type="number" value={planForm.max_flows} onChange={v => setPF("max_flows", v)} />
                    <TextField label="Max website widgets" type="number" value={planForm.max_widgets} onChange={v => setPF("max_widgets", v)} />
                    <TextField label="Max messages / month (-1 = off)" type="number" value={planForm.max_messages_month} onChange={v => setPF("max_messages_month", v)} />
                    <TextField label="Max API calls / month (-1 = off)" type="number" value={planForm.max_api_calls_month} onChange={v => setPF("max_api_calls_month", v)} />
                    <TextField label="Max storage MB (-1 = off)" type="number" value={planForm.max_storage_mb} onChange={v => setPF("max_storage_mb", v)} />

                    <h4 className="mg-form-section">Features</h4>
                    <CheckboxField label="Allow API access" checked={planForm.allow_api_access} onChange={v => setPF("allow_api_access", v)} />
                    <CheckboxField label="Allow custom branding" checked={planForm.allow_custom_branding} onChange={v => setPF("allow_custom_branding", v)} />
                    <CheckboxField label="Allow SSO" checked={planForm.allow_sso} onChange={v => setPF("allow_sso", v)} />
                    <CheckboxField label="Allow Flow Builder" checked={planForm.allow_flow_builder} onChange={v => setPF("allow_flow_builder", v)} />
                    <CheckboxField label="Allow Live Agent" checked={planForm.allow_live_agent} onChange={v => setPF("allow_live_agent", v)} />
                    <CheckboxField label="Allow Omnichannel" checked={planForm.allow_omnichannel} onChange={v => setPF("allow_omnichannel", v)} />
                    <CheckboxField label="Allow Advanced Analytics" checked={planForm.allow_advanced_analytics} onChange={v => setPF("allow_advanced_analytics", v)} />
                    <CheckboxField label="Allow LLM fallback" checked={planForm.allow_llm_fallback} onChange={v => setPF("allow_llm_fallback", v)} />
                    <CheckboxField label="Allow CSAT prompt" checked={planForm.allow_csat} onChange={v => setPF("allow_csat", v)} />

                    <h4 className="mg-form-section">Lifecycle</h4>
                    <TextField label="Free-trial length (days, 0 = no trial)" type="number" value={planForm.trial_days} onChange={v => setPF("trial_days", v)} />
                    <TextField label="Grace period after expiry (days)" type="number" value={planForm.grace_days} onChange={v => setPF("grace_days", v)} />
                </Modal>
            )}
        </div>
    );
}

const Stat = ({ label, value, tone }) => (
    <div className={`mg-stat ${tone || ""}`}>
        <div className="mg-stat-label">{label}</div>
        <div className="mg-stat-value">{value}</div>
    </div>
);
